import { httpsCallable } from 'firebase/functions';
import type { StaffRole, SyncSummary } from '@brands/shared';
import { firebase } from './firebase';

const call = <I, O>(name: string) =>
  httpsCallable<I, O>(firebase().functions, name, { timeout: 560_000 });

export const callables = {
  setAdmin: call<{ email: string; admin: boolean }, { email: string; admin: boolean }>('setAdmin'),
  createCampaign: call<
    { name: string; clientName: string; driveUrl: string; accent: string; brandId?: string },
    { id: string; folderName: string; clientSlug: string; slug: string }
  >('createCampaign'),
  deleteCampaign: call<{ campaignId: string }, { ok: boolean }>('deleteCampaign'),
  setMembers: call<{ campaignId: string; emails: string[] }, { emails: string[] }>('setMembers'),
  generateLanding: call<
    { campaignId: string },
    { costUsd: number; pendingCount: number; warnings: string[] }
  >('generateLanding'),
  publishLanding: call<{ campaignId: string; publish: boolean }, { status: string }>(
    'publishLanding',
  ),
  getAssetUrls: call<
    { campaignId: string; assetIds: string[]; variant: 'thumb' | 'large' | 'view' | 'download' },
    { urls: Record<string, string>; expiresInSeconds: number }
  >('getAssetUrls'),
  createZipLink: call<
    { campaignId: string; sectionId?: string },
    { url: string; files: number; bytes: number }
  >('createZipLink'),
  listAccess: call<
    Record<string, never>,
    {
      users: {
        uid: string;
        email: string;
        disabled: boolean;
        role: StaffRole | null;
        createdAt: string | null;
        lastSignInAt: string | null;
        campaigns: { id: string; name: string; status: string }[];
      }[];
      invitedWithoutAccount: string[];
    }
  >('listAccess'),
  createAccess: call<
    { email: string; password?: string; role?: StaffRole },
    { uid: string; email: string; password: string }
  >('createAccess'),
  resetAccessPassword: call<
    { uid: string; password?: string },
    { uid: string; email: string; password: string }
  >('resetAccessPassword'),
  setAccessDisabled: call<{ uid: string; disabled: boolean }, { uid: string; disabled: boolean }>(
    'setAccessDisabled',
  ),
  setAccessRole: call<{ uid: string; role: StaffRole | null }, { uid: string; role: StaffRole | null }>(
    'setAccessRole',
  ),
  deleteAccess: call<
    { uid: string; removeFromCampaigns: boolean },
    { uid: string; campaignsUpdated: number }
  >('deleteAccess'),
  setCampaignLink: call<
    { campaignId: string; clientSlug: string; slug: string },
    { clientSlug: string; slug: string }
  >('setCampaignLink'),
  createBrand: call<{ name: string; slug: string; accent: string; accent2?: string | null; rootUrl?: string }, { id: string; slug: string }>(
    'createBrand',
  ),
  updateBrand: call<{ brandId: string; name?: string; accent?: string; accent2?: string | null }, { ok: boolean }>('updateBrand'),
  deleteBrand: call<
    { brandId: string; deleteCampaigns: boolean },
    { campaigns: number; campaignsDeleted: number; domainRemoved: boolean }
  >('deleteBrand'),
  addBrandRoot: call<{ brandId: string; folderUrl: string }, { folderId: string; name: string }>('addBrandRoot'),
  removeBrandRoot: call<{ brandId: string; folderId: string }, { ok: boolean }>('removeBrandRoot'),
  scanBrand: call<{ brandId: string }, { created: { id: string; name: string; slug: string }[]; existing: number }>(
    'scanBrand',
  ),
  rerunCampaignPipeline: call<{ campaignId: string }, { ok: boolean }>('rerunCampaignPipeline'),
  setBrandMembers: call<
    { brandId: string; emails: string[] },
    { emails: string[]; removed: string[]; campaignsUpdated: number }
  >('setBrandMembers'),
  setBrandAccess: call<{ brandId: string; access: Record<string, string[]> }, { updated: number }>(
    'setBrandAccess',
  ),
  assignCampaignToBrand: call<{ campaignId: string; brandId: string }, { slug: string; addedToBrand: string[] }>(
    'assignCampaignToBrand',
  ),
  setupBrandDomain: call<{ brandId: string }, { host: string; state: string; detail: string | null }>(
    'setupBrandDomain',
  ),
  syncCampaign: call<{ campaignId: string; confirmLarge?: boolean }, SyncSummary>('syncCampaign'),
};
